import type { ChangeEvent, Dispatch, FormEvent, ReactNode, SetStateAction } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BriefcaseBusiness, CircleAlert, Download, Gift, Globe2, GraduationCap, HandHeart, Loader2, Send, Share2, SlidersHorizontal, Volume2 } from 'lucide-react';
import { honestBenefits, honestCareers, honestGrants, investorScope, speechAudio, studiesAbroad, supportCheckout, travelChat } from '../lib/api';
import type { BenefitResponse, CareerResponse, GrantResponse, InvestorScopeMessage, InvestorScopeResponse, InvestorScopeResult, LensRecord, OpportunityResult, PlaceLens, StudyMessage, StudyResponse, StudyResult, TravelMessage, TravelResponse, TravelResult, UncleChatMessage, UncleFallback } from '../lib/types';

type Mode = 'place' | 'invests' | 'grants' | 'benefits' | 'careers' | 'studies';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type ChatMessage =
  | { id: string; role: 'uncle'; kind: 'loading' | 'error'; text: string }
  | { id: string; role: 'uncle'; kind: 'fallback'; fallback: UncleFallback }
  | { id: string; role: 'uncle'; kind: 'service'; query: string; response: ServiceAnswer }
  | { id: string; role: 'user'; kind: 'text'; text: string }
  | { id: string; role: 'uncle'; kind: 'travel'; query: string; response: TravelResponse }
  | { id: string; role: 'uncle'; kind: 'invests'; query: string; response: InvestorScopeResponse }
  | { id: string; role: 'uncle'; kind: 'grants'; query: string; response: GrantResponse }
  | { id: string; role: 'uncle'; kind: 'benefits'; query: string; response: BenefitResponse }
  | { id: string; role: 'uncle'; kind: 'careers'; query: string; response: CareerResponse }
  | { id: string; role: 'uncle'; kind: 'studies'; query: string; response: StudyResponse };

type ServiceAnswer = {
  title: string;
  lead: string;
  sections: Array<{ title: string; body: string }>;
  links: Array<{ title: string; url: string; note: string }>;
  chips: string[];
};

type LensAdjustments = {
  african: number;
  caribbean: number;
  latinAmerican: number;
  localEveryday: number;
};

const defaultAdjustments: LensAdjustments = {
  african: 70,
  caribbean: 45,
  latinAmerican: 45,
  localEveryday: 70
};

const adjustmentLabels: Array<{ key: keyof LensAdjustments; label: string }> = [
  { key: 'african', label: 'African' },
  { key: 'caribbean', label: 'Caribbean' },
  { key: 'latinAmerican', label: 'Latin American' },
  { key: 'localEveryday', label: 'Local everyday' }
];

const placePrompts = [
  'Tell me about Lagos',
  'What should I know about Accra?',
  'Read Kingston for me',
  'What is Nairobi like?',
  'Give me São Paulo through a diaspora lens'
];

const investPrompts = [
  'I have a pitch I want Uncle to read',
  'Is this fundable or just busy?',
  'Help me sharpen who pays',
  'Where is the money logic weak?',
  'What would make this easier to back?'
];

const grantPrompts = [
  'Find grants for my nonprofit idea',
  'I need grant money for a community project',
  'Draft a grant outreach email',
  'What funders fit this work?',
  'Is this grant-ready?'
];

const benefitPrompts = [
  'What benefits might help me?',
  'I need help with food, rent, or utilities',
  'What documents should I gather?',
  'Help me find local support programs',
  'I had a life change and need a plan'
];

const careerPrompts = [
  'Translate my lived experience',
  'Turn informal work into résumé language',
  'Help me find the next career move',
  'I need training or certification options',
  'Help me navigate a job transition'
];

const studyPrompts = [
  'Help me find schools in the U.S., U.K., or Canada',
  'Scholarships for international students',
  'Scan my college essay',
  'Canada study permit basics',
  'U.K. personal statement help'
];

export function Home({ onStart }: { onStart: () => void }) {
  const [mode, setMode] = useState<Mode>(() => initialMode());
  const [modeChooserOpen, setModeChooserOpen] = useState(() => !new URLSearchParams(window.location.search).has('mode'));
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [investorTranscript, setInvestorTranscript] = useState<InvestorScopeMessage[]>([]);
  const [grantTranscript, setGrantTranscript] = useState<UncleChatMessage[]>([]);
  const [benefitTranscript, setBenefitTranscript] = useState<UncleChatMessage[]>([]);
  const [careerTranscript, setCareerTranscript] = useState<UncleChatMessage[]>([]);
  const [travelTranscript, setTravelTranscript] = useState<TravelMessage[]>([]);
  const [studyTranscript, setStudyTranscript] = useState<StudyMessage[]>([]);
  const [adjustments, setAdjustments] = useState<LensAdjustments>(defaultAdjustments);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installMessage, setInstallMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const prompts = mode === 'invests'
    ? investPrompts
    : mode === 'grants'
      ? grantPrompts
      : mode === 'benefits'
        ? benefitPrompts
        : mode === 'careers'
          ? careerPrompts
          : mode === 'studies'
            ? studyPrompts
            : placePrompts;
  const placeholder = mode === 'invests'
      ? 'Tell me what you’re building...'
      : mode === 'grants'
        ? 'Tell me the project, funder, deadline, or grant need...'
        : mode === 'benefits'
          ? 'Tell me your state, household, need, and what changed...'
          : mode === 'careers'
            ? 'Tell me your work history, goals, location, or next move...'
            : mode === 'studies'
              ? 'Ask about schools, scholarships, visas, essays...'
              : 'Ask about Lagos, Accra, Kingston...';

  function chooseMode(nextMode: Mode) {
    setMode(nextMode);
    setModeChooserOpen(false);
    window.setTimeout(() => inputRef.current?.focus(), 50);
  }

  function changeAdjustment(key: keyof LensAdjustments, event: ChangeEvent<HTMLInputElement>) {
    setAdjustments((current) => ({ ...current, [key]: Number(event.target.value) }));
  }

  useEffect(() => {
    const installed = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone;
    if (installed) {
      setInstallMessage('Uncle is already with you.');
      return undefined;
    }

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
      setInstallMessage('');
    }

    function onInstalled() {
      setInstallPrompt(null);
      setInstallMessage('Uncle is with you now.');
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function takeUncleWithYou() {
    if (installPrompt) {
      const prompt = installPrompt;
      setInstallPrompt(null);
      await prompt.prompt();
      const choice = await prompt.userChoice.catch(() => ({ outcome: 'dismissed' as const, platform: '' }));
      setInstallMessage(choice.outcome === 'accepted' ? 'Uncle is with you now.' : 'No worries. Uncle will be here.');
      return;
    }

    setInstallMessage('On mobile, use Share → Add to Home Screen. Uncle travels light.');
  }

  async function ask(event?: FormEvent<HTMLFormElement>, prompt?: string) {
    event?.preventDefault();
    const text = (prompt || query).trim();
    if (!text || loading) return;

    const startedAt = Date.now();
    const typingTimers: number[] = [];
    setQuery('');
    setLoading(true);
    setMessages((current) => [
      ...current.filter((message) => message.kind !== 'loading'),
      { id: crypto.randomUUID(), role: 'user', kind: 'text', text },
      {
        id: 'loading',
        role: 'uncle',
        kind: 'loading',
        text: 'Uncle is typing.'
      }
    ]);
    typingTimers.push(...startTypingTheater(mode, text, setMessages));

    try {
      if (isAboutUncleQuery(text)) {
        await minimumThinkingTime(startedAt);
        setMessages((current) => [
          ...current.filter((message) => message.kind !== 'loading'),
          { id: crypto.randomUUID(), role: 'uncle', kind: 'service', query: text, response: buildServiceAnswer(text) }
        ]);
      } else if (mode === 'invests') {
        const nextTranscript: InvestorScopeMessage[] = [...investorTranscript, { role: 'user', content: text }];
        const response = await investorScope(nextTranscript);
        await minimumThinkingTime(startedAt);
        const updatedTranscript = response.mode === 'investor_ask'
          ? [...nextTranscript, { role: 'assistant' as const, content: response.message }]
          : nextTranscript;
        setInvestorTranscript(updatedTranscript);
        setMessages((current) => [
          ...current.filter((message) => message.kind !== 'loading'),
          { id: crypto.randomUUID(), role: 'uncle', kind: 'invests', query: text, response }
        ]);
      } else if (mode === 'grants') {
        const nextTranscript: UncleChatMessage[] = [...grantTranscript, { role: 'user', content: text }];
        const response = await honestGrants(nextTranscript);
        await minimumThinkingTime(startedAt);
        const updatedTranscript = response.mode === 'grant_ask'
          ? [...nextTranscript, { role: 'assistant' as const, content: response.message }]
          : nextTranscript;
        setGrantTranscript(updatedTranscript);
        setMessages((current) => [
          ...current.filter((message) => message.kind !== 'loading'),
          { id: crypto.randomUUID(), role: 'uncle', kind: 'grants', query: text, response }
        ]);
      } else if (mode === 'benefits') {
        const nextTranscript: UncleChatMessage[] = [...benefitTranscript, { role: 'user', content: text }];
        const response = await honestBenefits(nextTranscript);
        await minimumThinkingTime(startedAt);
        const updatedTranscript = response.mode === 'benefit_ask'
          ? [...nextTranscript, { role: 'assistant' as const, content: response.message }]
          : nextTranscript;
        setBenefitTranscript(updatedTranscript);
        setMessages((current) => [
          ...current.filter((message) => message.kind !== 'loading'),
          { id: crypto.randomUUID(), role: 'uncle', kind: 'benefits', query: text, response }
        ]);
      } else if (mode === 'careers') {
        const nextTranscript: UncleChatMessage[] = [...careerTranscript, { role: 'user', content: text }];
        const response = await honestCareers(nextTranscript);
        await minimumThinkingTime(startedAt);
        const updatedTranscript = response.mode === 'career_ask'
          ? [...nextTranscript, { role: 'assistant' as const, content: response.message }]
          : nextTranscript;
        setCareerTranscript(updatedTranscript);
        setMessages((current) => [
          ...current.filter((message) => message.kind !== 'loading'),
          { id: crypto.randomUUID(), role: 'uncle', kind: 'careers', query: text, response }
        ]);
      } else if (mode === 'studies') {
        const nextTranscript: StudyMessage[] = [...studyTranscript, { role: 'user', content: text }];
        const response = await studiesAbroad(nextTranscript);
        await minimumThinkingTime(startedAt);
        const updatedTranscript = response.mode === 'study_ask'
          ? [...nextTranscript, { role: 'assistant' as const, content: response.message }]
          : nextTranscript;
        setStudyTranscript(updatedTranscript);
        setMessages((current) => [
          ...current.filter((message) => message.kind !== 'loading'),
          { id: crypto.randomUUID(), role: 'uncle', kind: 'studies', query: text, response }
        ]);
      } else {
        const shouldStartNewPlaceRead = travelTranscript.length > 1 && looksLikeNewPlaceQuery(text);
        const baseTranscript = shouldStartNewPlaceRead ? [] : travelTranscript;
        const nextTranscript: TravelMessage[] = [...baseTranscript, { role: 'user', content: text }];
        const response = await travelChat(nextTranscript, adjustments);
        await minimumThinkingTime(startedAt);
        const updatedTranscript = response.mode === 'travel_ask'
          ? [...nextTranscript, { role: 'assistant' as const, content: response.message }]
          : nextTranscript;
        setTravelTranscript(updatedTranscript);
        setMessages((current) => [
          ...current.filter((message) => message.kind !== 'loading'),
          { id: crypto.randomUUID(), role: 'uncle', kind: 'travel', query: text, response }
        ]);
      }
    } catch (err) {
      setMessages((current) => [
        ...current.filter((message) => message.kind !== 'loading'),
        {
          id: crypto.randomUUID(),
          role: 'uncle',
          kind: 'fallback',
          fallback: makeLocalUncleFallback(text, err instanceof Error ? err.message : undefined)
        }
      ]);
    } finally {
      typingTimers.forEach((timer) => window.clearTimeout(timer));
      setLoading(false);
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  return (
    <main className="relative min-h-[calc(100vh-5.5rem)] overflow-hidden bg-charcoal bg-[image:linear-gradient(90deg,rgba(10,9,7,0.86)_0%,rgba(10,9,7,0.62)_42%,rgba(10,9,7,0.24)_100%),url('/uncle_honest.png')] bg-cover bg-center px-4 py-5 md:bg-[position:center] md:px-8 md:py-8">
      <div className="absolute right-5 top-16 z-20 flex w-[min(22rem,calc(100vw-2.5rem))] flex-col items-end gap-2 md:right-8">
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => void takeUncleWithYou()}
            className="focus-ring grid h-9 w-9 place-items-center rounded-full border border-warm bg-warm text-charcoal shadow-[0_12px_36px_rgba(0,0,0,0.28)] transition hover:bg-bone"
            aria-label="Take Uncle with you"
            title="Take Uncle with you"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setAboutOpen((current) => !current)}
            className="focus-ring rounded-full border border-[#1F7A3F] bg-[#1F7A3F] px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.08em] text-warm shadow-[0_12px_36px_rgba(0,0,0,0.28)] transition hover:border-[#2D8E4D] hover:bg-[#2D8E4D]"
            aria-label="About Uncle"
            aria-expanded={aboutOpen}
          >
            About Uncle
          </button>
        </div>
        {installMessage ? <p className="max-w-72 rounded-2xl bg-charcoal/72 px-3 py-2 text-right text-xs leading-5 text-warm shadow-[0_12px_36px_rgba(0,0,0,0.2)] backdrop-blur">{installMessage}</p> : null}
        {aboutOpen ? (
          <AboutUnclePanel onInstall={() => void takeUncleWithYou()} />
        ) : null}
      </div>

      <section className="mx-auto flex min-h-[calc(100vh-8rem)] max-w-6xl items-center justify-center md:justify-start">
        <section
          className="chat-float fade-in flex h-[calc(100vh-8rem)] min-h-[38rem] w-full max-w-[31rem] flex-col overflow-hidden rounded-[2rem] border border-warm/15 bg-warm shadow-[0_32px_120px_rgba(0,0,0,0.48)]"
          aria-label="UncleHonest chat"
        >
          <div className="border-b border-charcoal/10 bg-warm px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#1F7A3F] font-serif text-xl font-semibold text-warm" aria-label="UncleHonest logo">u</div>
                <div className="min-w-0">
                  <p className="truncate font-serif text-lg font-semibold leading-none tracking-[-0.045em] text-charcoal">UncleHonest</p>
                  <p className="mt-1 flex items-center gap-2 font-mono text-[10px] uppercase text-charcoal/42">
                    <span className="presence-light" aria-hidden="true" />
                    <span>uncle is home</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setSettingsOpen((current) => !current)}
                  className="focus-ring grid h-9 w-9 place-items-center rounded-full text-charcoal/55 transition hover:bg-bone hover:text-charcoal"
                  aria-label="Open settings"
                  aria-expanded={settingsOpen}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </button>
              </div>
            </div>

            {modeChooserOpen ? (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <ModeCard active={mode === 'invests'} icon={<span className="text-sm leading-none">$</span>} label="Invests" note="fundability" onClick={() => chooseMode('invests')} />
                <ModeCard active={mode === 'grants'} icon={<Gift className="h-3.5 w-3.5" />} label="Grants" note="funder fit" onClick={() => chooseMode('grants')} />
                <ModeCard active={mode === 'benefits'} icon={<HandHeart className="h-3.5 w-3.5" />} label="Benefits" note="support now" onClick={() => chooseMode('benefits')} />
                <ModeCard active={mode === 'careers'} icon={<BriefcaseBusiness className="h-3.5 w-3.5" />} label="Careers" note="next move" onClick={() => chooseMode('careers')} />
                <ModeCard active={mode === 'studies'} icon={<GraduationCap className="h-3.5 w-3.5" />} label="Studies" note="study abroad" onClick={() => chooseMode('studies')} />
                <ModeCard active={mode === 'place'} icon={<Globe2 className="h-3.5 w-3.5" />} label="Travels" note="land well" onClick={() => chooseMode('place')} />
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-between gap-2 rounded-full border border-charcoal/8 bg-bone px-2 py-1.5">
                <span className="inline-flex min-w-0 items-center gap-2 px-2">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#1F7A3F] text-warm">{modeIcon(mode)}</span>
                  <span className="truncate text-xs font-semibold text-charcoal">{modeLabel(mode)}</span>
                </span>
                <button type="button" onClick={() => setModeChooserOpen(true)} className="focus-ring rounded-full px-3 py-1.5 font-mono text-[10px] uppercase text-charcoal/45 transition hover:bg-warm hover:text-charcoal">
                  change
                </button>
              </div>
            )}
          </div>

          {settingsOpen ? (
            <div className="border-b border-charcoal/10 bg-bone/80 px-4 py-2.5">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="font-mono text-[10px] uppercase text-charcoal/45">{modeLabel(mode)} settings</p>
                {mode === 'place' ? <button type="button" onClick={() => setAdjustments(defaultAdjustments)} className="focus-ring text-xs font-semibold text-charcoal/55 hover:text-charcoal">Reset</button> : null}
              </div>
              {mode === 'place' ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {adjustmentLabels.map((item) => (
                    <label key={item.key} className="grid gap-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-charcoal/70">{item.label}</span>
                        <span className="font-mono text-[10px] uppercase text-charcoal/40">{adjustments[item.key]}%</span>
                      </span>
                      <input type="range" min="0" max="100" step="10" value={adjustments[item.key]} onChange={(event) => changeAdjustment(item.key, event)} className="h-4 max-w-32 accent-charcoal" aria-label={`${item.label} lens strength`} />
                    </label>
                  ))}
                </div>
              ) : mode === 'invests' ? (
                <p className="text-xs leading-5 text-charcoal/55">Honest Invests reads the money logic: who pays, why it matters, what could slow adoption, what it costs to deliver, and whether the story is clear enough to back.</p>
              ) : mode === 'grants' ? (
                <p className="text-xs leading-5 text-charcoal/55">Honest Grants helps shape a project, identify funder fit, check readiness, and draft practical outreach or application language.</p>
              ) : mode === 'benefits' ? (
                <p className="text-xs leading-5 text-charcoal/55">Honest Benefits helps people sort food, rent, utility, healthcare, childcare, and emergency-support programs without pretending eligibility is guaranteed.</p>
              ) : mode === 'careers' ? (
                <p className="text-xs leading-5 text-charcoal/55">Honest Careers helps translate experience into a next move: positioning, skills, training, job search strategy, and a clearer work story.</p>
              ) : mode === 'studies' ? (
                <p className="text-xs leading-5 text-charcoal/55">Honest Studies helps students and families from African, Caribbean, and Latin American regions navigate U.S., U.K., and Canadian schools, scholarships, essays, applications, and visa/study-permit questions.</p>
              ) : null}
            </div>
          ) : null}

          <div className="flex-1 overflow-y-auto bg-[#FCFAF5] px-3 py-4">
            <div className="flex flex-col gap-3">
              <ModeIntro mode={mode} />
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} onPrompt={(value) => void ask(undefined, value)} />
              ))}
            </div>
          </div>

          <div className="border-t border-charcoal/10 bg-warm px-3 py-3">
            <div className="mb-2 flex gap-2 overflow-x-auto pb-1">
              {prompts.map((prompt) => (
                <button key={prompt} type="button" onClick={() => void ask(undefined, prompt)} className="focus-ring shrink-0 rounded-full border border-charcoal/10 bg-bone px-3 py-1.5 text-left text-[11px] text-charcoal/58 transition hover:border-charcoal/25 hover:text-charcoal">
                  {prompt}
                </button>
              ))}
            </div>

            <form onSubmit={ask} className="flex items-center gap-2">
              <label className="sr-only" htmlFor="uncle-chat">Ask UncleHonest</label>
              <input
                ref={inputRef}
                id="uncle-chat"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder}
                className="focus-ring min-h-12 flex-1 rounded-full border border-charcoal/12 bg-bone px-4 text-sm text-charcoal placeholder:text-charcoal/38"
              />
              <button type="submit" disabled={loading || !query.trim()} className="focus-ring grid h-12 w-12 shrink-0 place-items-center rounded-full bg-charcoal text-warm transition hover:bg-cocoa disabled:cursor-not-allowed disabled:opacity-45" aria-label="Send message">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </form>
          </div>
        </section>
      </section>
    </main>
  );
}

function ModeCard({ active, icon, label, note, onClick }: { active: boolean; icon: ReactNode; label: string; note: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`focus-ring rounded-2xl border p-2.5 text-left transition ${active ? 'border-charcoal bg-charcoal text-warm shadow-[0_10px_28px_rgba(29,27,23,0.14)]' : 'border-charcoal/8 bg-bone text-charcoal/64 hover:border-charcoal/18 hover:bg-stone/15 hover:text-charcoal'}`}
    >
      <span className="flex items-center gap-2">
        <span className={`grid h-7 w-7 place-items-center rounded-full ${active ? 'bg-warm/12 text-warm' : 'bg-warm text-[#1F7A3F]'}`}>{icon}</span>
        <span className="text-xs font-semibold leading-none">{label}</span>
      </span>
      <span className={`mt-1 block font-mono text-[9px] uppercase tracking-[0.06em] ${active ? 'text-warm/54' : 'text-charcoal/38'}`}>{note}</span>
    </button>
  );
}

function AboutUnclePanel({ onInstall }: { onInstall: () => void }) {
  const [supportStatus, setSupportStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  async function support() {
    setSupportStatus('loading');
    try {
      const checkout = await supportCheckout();
      window.location.assign(checkout.url);
    } catch {
      setSupportStatus('error');
    }
  }

  return (
    <div className="mt-3 rounded-2xl border border-charcoal/8 bg-bone p-3">
      <p className="font-mono text-[10px] uppercase text-charcoal/45">about uncle</p>
      <p className="mt-2 text-sm leading-6 text-charcoal/66">
        UncleHonest is a free, human-led, AI-powered tool from{' '}
        <a href="https://www.thebirthrightproject.org/softerbread" target="_blank" rel="noreferrer" className="underline decoration-charcoal/25 underline-offset-4 hover:text-cocoa">Softerbread</a>,
        {' '}the financial co-op initiative of{' '}
        <a href="https://www.thebirthrightproject.org/" target="_blank" rel="noreferrer" className="underline decoration-charcoal/25 underline-offset-4 hover:text-cocoa">The Birthright Project</a>,
        {' '}a 501(c)(3) nonprofit.
      </p>
      <p className="mt-2 text-sm leading-6 text-charcoal/60">Uncle helps people read places, pressure-test ideas, find grants, sort benefits, shape career moves, study abroad, and move with more context than the algorithm usually gives.</p>
      <div className="mt-3 grid gap-2">
        <button type="button" onClick={onInstall} className="focus-ring rounded-full border border-charcoal/10 bg-warm px-3 py-2 text-center font-mono text-[10px] uppercase text-charcoal/62 transition hover:border-[#1F7A3F]/45 hover:text-[#1F7A3F]">
          Take Uncle with you
        </button>
        <a className="focus-ring rounded-full border border-charcoal/10 bg-warm px-3 py-2 text-center font-mono text-[10px] uppercase text-charcoal/62 transition hover:border-[#1F7A3F]/45 hover:text-[#1F7A3F]" href="mailto:hello@unclehonest.org?subject=Sign%20me%20up%20for%20Honest%20Notes">
          Honest Notes
        </a>
        <button type="button" onClick={() => void support()} disabled={supportStatus === 'loading'} className="focus-ring rounded-full bg-[#1F7A3F] px-3 py-2 font-mono text-[10px] uppercase text-warm transition hover:bg-charcoal disabled:opacity-50">
          {supportStatus === 'loading' ? 'Opening Stripe…' : 'Support this concept'}
        </button>
        <span className="text-center font-mono text-[10px] uppercase text-charcoal/40">Keep Uncle free</span>
      </div>
      {supportStatus === 'error' ? <p className="mt-2 text-xs leading-5 text-clay">Stripe checkout is not ready yet.</p> : null}
    </div>
  );
}

function ModeIntro({ mode }: { mode: Mode }) {
  return (
    <div className="rounded-[1.25rem] rounded-bl-sm border border-charcoal/8 bg-warm p-4 shadow-[0_12px_40px_rgba(29,27,23,0.05)]">
      <span className="mb-3 inline-flex rounded-full border border-[#1F7A3F]/25 bg-[#1F7A3F]/8 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[#1F7A3F]">
        beta v1: building in public
      </span>
      <p className="font-serif text-2xl italic leading-7 text-cocoa">Have we met? I’m UncleHonest.</p>
      {mode === 'invests' ? (
        <>
          <p className="mt-4 font-mono text-[10px] uppercase text-charcoal/45">honest invests</p>
          <h2 className="mt-2 font-serif text-3xl leading-none tracking-[-0.04em]">Will someone fund this “thing”?</h2>
          <p className="mt-3 text-sm leading-6 text-charcoal/70">Tell me what you’re building, or paste a pitch/write-up. I’ll read the buyer, the value, the risks, the costs, and the story — then give you an honest readiness score.</p>
        </>
      ) : mode === 'grants' ? (
        <>
          <p className="mt-4 font-mono text-[10px] uppercase text-charcoal/45">honest grants</p>
          <h2 className="mt-2 font-serif text-3xl leading-none tracking-[-0.04em]">Make the ask fit the work.</h2>
          <p className="mt-3 text-sm leading-6 text-charcoal/70">Tell me the project, community, budget, deadline, or funder. Uncle will read fit, readiness, language, and the next honest move.</p>
        </>
      ) : mode === 'benefits' ? (
        <>
          <p className="mt-4 font-mono text-[10px] uppercase text-charcoal/45">honest benefits</p>
          <h2 className="mt-2 font-serif text-3xl leading-none tracking-[-0.04em]">Find the help without the fog.</h2>
          <p className="mt-3 text-sm leading-6 text-charcoal/70">Tell me your state, household, need, and what changed. Uncle will help sort possible programs, documents, and next steps — carefully, not magically.</p>
        </>
      ) : mode === 'careers' ? (
        <>
          <p className="mt-4 font-mono text-[10px] uppercase text-charcoal/45">honest careers</p>
          <h2 className="mt-2 font-serif text-3xl leading-none tracking-[-0.04em]">Work has a story. Let’s read it.</h2>
          <p className="mt-3 text-sm leading-6 text-charcoal/70">Tell me where you’ve been, what you can do, and what kind of life the work needs to support. Uncle will help shape the next move.</p>
        </>
      ) : mode === 'studies' ? (
        <>
          <p className="mt-4 font-mono text-[10px] uppercase text-charcoal/45">honest studies abroad</p>
          <h2 className="mt-2 font-serif text-3xl leading-none tracking-[-0.04em]">Study far, study wide, study deep; return home or not?</h2>
          <p className="mt-3 text-sm leading-6 text-charcoal/70">Ask about U.S., U.K., or Canadian schools, scholarships, essays, visas, study permits, and how to choose a path that actually fits the student.</p>
        </>
      ) : (
        <>
          <p className="mt-4 font-mono text-[10px] uppercase text-charcoal/45">honest travels</p>
          <h2 className="mt-2 font-serif text-3xl leading-none tracking-[-0.04em]">Land well.</h2>
          <p className="mt-3 text-sm leading-6 text-charcoal/70">Name a city first. Then I’ll ask what kind of read you want — food, relocation, markets, creative life, first 48 hours — so you don’t get the same guide every time.</p>
        </>
      )}
    </div>
  );
}

function MessageBubble({ message, onPrompt }: { message: ChatMessage; onPrompt: (value: string) => void }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[82%] rounded-[1.25rem] rounded-br-sm bg-charcoal px-4 py-3 text-sm leading-6 text-warm">{message.text}</p>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] rounded-[1.25rem] rounded-bl-sm border border-charcoal/8 bg-warm p-4 text-charcoal shadow-[0_12px_40px_rgba(29,27,23,0.05)]">
        {message.kind === 'loading' ? <ThinkingBubble text={message.text} /> : null}
        {message.kind === 'fallback' ? <UncleFallbackAnswer fallback={message.fallback} onPrompt={onPrompt} /> : null}
        {message.kind === 'service' ? <ServiceAnswerView answer={message.response} onPrompt={onPrompt} /> : null}
        {message.kind === 'error' ? (
          <div className="flex items-start gap-3">
            <CircleAlert className="mt-1 h-4 w-4 shrink-0 text-clay" />
            <p className="text-sm leading-6 text-charcoal/72">{message.text}</p>
          </div>
        ) : null}
        {message.kind === 'travel' ? <TravelAnswer response={message.response} onPrompt={onPrompt} /> : null}
        {message.kind === 'invests' ? <InvestorAnswer response={message.response} /> : null}
        {message.kind === 'grants' ? <OpportunityAnswer response={message.response} lane="grants" onPrompt={onPrompt} /> : null}
        {message.kind === 'benefits' ? <OpportunityAnswer response={message.response} lane="benefits" onPrompt={onPrompt} /> : null}
        {message.kind === 'careers' ? <OpportunityAnswer response={message.response} lane="careers" onPrompt={onPrompt} /> : null}
        {message.kind === 'studies' ? <StudiesAnswer response={message.response} onPrompt={onPrompt} /> : null}
      </div>
    </div>
  );
}

function ThinkingBubble({ text }: { text: string }) {
  return (
    <div className="min-w-48">
      <div className="flex items-center gap-2">
        <span className="thinking-dot" />
        <span className="thinking-dot" />
        <span className="thinking-dot" />
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase text-charcoal/45">{text}</p>
    </div>
  );
}

function startTypingTheater(mode: Mode, text: string, setMessages: Dispatch<SetStateAction<ChatMessage[]>>) {
  const beats = mode === 'invests'
    ? [
        'Uncle is reading it like money is shy.',
        'Uncle typed a sharper sentence, then deleted it.',
        'Uncle is checking where the real risk is hiding.'
      ]
    : mode === 'grants'
      ? [
          'Uncle is reading the project and the funder at the same table.',
          'Uncle wrote “impact,” paused, and asked for proof.',
          'Uncle is checking what a reviewer would need to believe.'
        ]
    : mode === 'benefits'
      ? [
          'Uncle is sorting need, timing, documents, and eligibility.',
          'Uncle is careful here. Benefits advice should not be loose.',
          'Uncle is looking for the next practical door.'
        ]
    : mode === 'careers'
      ? [
          'Uncle is reading the work story underneath the résumé.',
          'Uncle deleted a fancy phrase. Plain truth works better.',
          'Uncle is checking skill, market, timing, and next move.'
        ]
    : mode === 'studies'
      ? [
          'Uncle is checking the school path, not just the brochure.',
          'Uncle wrote “prestige,” paused, and erased it.',
          'Uncle is lining up fit, funding, paperwork, and the student’s real story.'
        ]
    : [
        `Uncle is walking around ${cleanPlaceQuery(text)} in memory.`,
        'Uncle started with a shortcut, frowned, and erased it.',
        'Uncle is pulling the place back down to the ground.'
      ];

  return beats.map((beat, index) => window.setTimeout(() => {
    setMessages((current) => current.map((message) => (
      message.id === 'loading' && message.kind === 'loading' ? { ...message, text: beat } : message
    )));
  }, 650 + index * 850));
}

function minimumThinkingTime(startedAt: number) {
  const remaining = 2850 - (Date.now() - startedAt);
  return remaining > 0 ? new Promise((resolve) => window.setTimeout(resolve, remaining)) : Promise.resolve();
}

function InvestorAnswer({ response }: { response: InvestorScopeResponse }) {
  if (response.mode === 'investor_ask') {
    return (
      <div>
        <p className="font-mono text-[10px] uppercase text-charcoal/45">
          honest invests
        </p>
        <p className="mt-3 text-sm leading-6 text-charcoal/72">{uncleAskPrefix(response)} {response.message}</p>
        {response.question_num === 3 || response.question_num === 6 ? <UncleDigression kind="invests" /> : null}
      </div>
    );
  }

  return <InvestorResultView result={response} />;
}

function InvestorResultView({ result }: { result: InvestorScopeResult }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">investor(o)scope result</p>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-5xl leading-none tracking-[-0.05em]">{result.overall}<span className="text-2xl text-charcoal/40">/100</span></h1>
          <p className={`mt-2 inline-flex rounded-full px-3 py-1 font-mono text-[10px] uppercase ${verdictClass(result.verdict)}`}>{result.verdict}</p>
        </div>
        {result.from_doc ? <p className="font-mono text-[10px] uppercase text-charcoal/40">from document</p> : null}
      </div>

      <p className="mt-4 text-sm leading-6 text-charcoal/72">{result.rationale}</p>
      <UncleDigression kind="invests" />

      <section className="mt-5 rounded-2xl border border-charcoal/8 bg-bone p-3">
        <p className="font-mono text-[10px] uppercase text-charcoal/45">market math</p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <DataPoint value={result.market_math.reachable_people} label="reachable" />
          <DataPoint value={`$${Math.round(result.market_math.price_point)}`} label="price" />
          <DataPoint value={`$${result.market_math.projected_monthly}`} label="projected/mo" />
        </div>
        {result.market_math.notes ? <p className="mt-3 text-xs leading-5 text-charcoal/50">{result.market_math.notes}</p> : null}
      </section>

      {result.returns_speed.applies ? (
        <section className="mt-4 rounded-2xl border border-charcoal/8 bg-bone p-3">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">returns speed</p>
          <p className="mt-2 text-sm font-semibold text-charcoal">{result.returns_speed.verdict}</p>
          <p className="mt-1 text-sm leading-6 text-charcoal/64">{result.returns_speed.note}</p>
        </section>
      ) : null}

      <section className="mt-5">
        <p className="font-mono text-[10px] uppercase text-charcoal/45">readiness signals</p>
        <div className="mt-2 grid gap-3">
          {result.scores.map((score) => (
            <article key={score.q} className="border-t border-charcoal/10 pt-3">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-sm font-semibold text-charcoal">{score.label}</h2>
                <span className="font-mono text-xs text-clay">{score.score}</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-charcoal/10">
                <div className="h-full rounded-full bg-clay" style={{ width: `${score.score}%` }} />
              </div>
              <p className="mt-2 text-xs leading-5 text-charcoal/60">{score.note}</p>
              <p className="mt-1 text-xs leading-5 text-cocoa">Improve: {score.improve}</p>
            </article>
          ))}
        </div>
      </section>

      {result.next_steps.length ? (
        <section className="mt-5 rounded-2xl border border-charcoal/8 bg-bone p-3">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">next steps</p>
          <ul className="mt-2 grid gap-2">
            {result.next_steps.map((step) => (
              <li key={step} className="text-sm leading-6 text-charcoal/70">• {step}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <ShareActions title="Honest Invests" text={investorShareText(result)} filename="honest-invests.txt" />
    </div>
  );
}

function UncleDigression({ kind, city }: { kind: 'travels' | 'invests'; city?: string }) {
  const place = city || 'a city like this';
  const options = kind === 'travels'
    ? [
        `A place teaches softly first. In ${place}, listen for the market, the morning errand, the second street. What was I saying… yes: start where daily life is visible.`,
        `As the ancient Chinese wisdom says… wait, no, that one is closer to West African road wisdom: the eye that travels sees what the mouth cannot explain. What was I saying… let the ordinary streets correct the itinerary.`,
        `Sometimes the best door is not marked. It is the place people keep returning to without being told. What was I saying… choose living rooms, not just photo spots.`
      ]
    : [
        'A beautiful idea still has to find the person who pays. What was I saying… yes: buyer, cost, risk, and reach.',
        'As the ancient Chinese wisdom says… no, no, Uncle is mixing drawers. Old market wisdom: the basket can be beautiful, but someone must carry it home. What was I saying… make the value plain enough that the buyer can repeat it.',
        'A pitch can shine like Sunday shoes and still have no road to the customer. What was I saying… traction beats decoration.'
      ];
  const copy = options[Math.abs(hashString(`${kind}:${place}`)) % options.length];

  return (
    <aside className="mt-4 rounded-2xl border border-clay/20 bg-clay/8 p-3">
      <p className="font-serif text-lg italic leading-6 text-cocoa">{copy}</p>
    </aside>
  );
}

function hashString(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

function uncleAskPrefix(response: Extract<InvestorScopeResponse, { mode: 'investor_ask' }>) {
  if (!response.question_num) return 'Mmh. Good. Before we make it formal, let me ask this gently.';
  if (response.question_num === 1) return 'Alright. Let’s start where the money starts.';
  if (response.question_num === response.total) return 'One more thing. Don’t round it up to make it pretty.';
  return 'Good. Let me sharpen the read.';
}

function ServiceAnswerView({ answer, onPrompt }: { answer: ServiceAnswer; onPrompt: (value: string) => void }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">about uncle</p>
      <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">{answer.title}</h1>
      <p className="mt-3 text-sm leading-6 text-charcoal/72">{answer.lead}</p>

      <div className="mt-5 grid gap-4">
        {answer.sections.map((section) => (
          <section key={section.title} className="rounded-2xl border border-charcoal/8 bg-bone p-3">
            <p className="font-mono text-[10px] uppercase text-charcoal/45">{section.title}</p>
            <p className="mt-2 text-sm leading-6 text-charcoal/66">{section.body}</p>
          </section>
        ))}
      </div>

      {answer.links.length ? (
        <section className="mt-5 rounded-2xl border border-charcoal/8 bg-bone p-3">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">links</p>
          <div className="mt-2 grid gap-3">
            {answer.links.map((link) => (
              <a key={link.title} href={link.url} target="_blank" rel="noreferrer" className="focus-ring block rounded-2xl bg-warm p-3 transition hover:bg-stone/20">
                <span className="text-sm font-semibold text-charcoal">{link.title}</span>
                <span className="mt-1 block text-xs leading-5 text-charcoal/56">{link.note}</span>
              </a>
            ))}
          </div>
        </section>
      ) : null}

      {answer.chips.length ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {answer.chips.map((chip) => (
            <button key={chip} type="button" onClick={() => onPrompt(chip)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {chip}
            </button>
          ))}
        </div>
      ) : null}

      <ShareActions title="About UncleHonest" text={serviceShareText(answer)} filename="about-uncle-honest.txt" />
    </div>
  );
}

function UncleFallbackAnswer({ fallback, onPrompt }: { fallback: UncleFallback; onPrompt: (value: string) => void }) {
  const text = uncleFallbackShareText(fallback);

  return (
    <div>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">uncle’s to-do list</p>
      <p className="mt-2 font-serif text-2xl italic leading-8 text-cocoa">“{fallback.proverb}”</p>
      <p className="mt-3 text-sm leading-6 text-charcoal/72">{fallback.message}</p>
      <p className="mt-2 text-sm leading-6 text-charcoal/62">{fallback.todoPrompt}</p>
      {fallback.chips.length ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {fallback.chips.map((chip) => (
            <button key={chip} type="button" onClick={() => onPrompt(chip)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {chip}
            </button>
          ))}
        </div>
      ) : null}
      <ShareActions title="UncleHonest to-do note" text={text} filename="uncle-honest-todo.txt" />
    </div>
  );
}

function OpportunityAnswer({ response, lane, onPrompt }: { response: GrantResponse | BenefitResponse | CareerResponse; lane: 'grants' | 'benefits' | 'careers'; onPrompt: (value: string) => void }) {
  if (response.mode === 'uncle_fallback') {
    return <UncleFallbackAnswer fallback={response} onPrompt={onPrompt} />;
  }

  const label = lane === 'grants' ? 'honest grants' : lane === 'benefits' ? 'honest benefits' : 'honest careers';
  const title = lane === 'grants' ? 'What fits the work?' : lane === 'benefits' ? 'What help is actually reachable?' : 'What is the next honest move?';

  if (response.mode === 'grant_ask' || response.mode === 'benefit_ask' || response.mode === 'career_ask') {
    return (
      <div>
        <p className="font-mono text-[10px] uppercase text-charcoal/45">{label}</p>
        <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-charcoal/72">{response.message}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {response.chips.map((chip: string) => (
            <button key={chip} type="button" onClick={() => onPrompt(chip)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {chip}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return <OpportunityResultView result={response as OpportunityResult} lane={lane} onPrompt={onPrompt} />;
}

function OpportunityResultView({ result, lane, onPrompt }: { result: OpportunityResult; lane: 'grants' | 'benefits' | 'careers'; onPrompt: (value: string) => void }) {
  const label = lane === 'grants' ? 'honest grants' : lane === 'benefits' ? 'honest benefits' : 'honest careers';

  return (
    <div>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">{label} · {result.topic}</p>
      <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">{result.lead}</h1>

      <section className="mt-4 rounded-2xl border border-charcoal/8 bg-bone p-3">
        <p className="font-mono text-[10px] uppercase text-charcoal/45">uncle’s read</p>
        <p className="mt-2 text-sm leading-6 text-charcoal/66">{result.read}</p>
      </section>

      {result.matches.length ? (
        <section className="mt-5">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">{lane === 'careers' ? 'paths to consider' : 'matches to check'}</p>
          <div className="mt-2 grid gap-3">
            {result.matches.map((match) => (
              <article key={`${match.title}:${match.type}`} className="rounded-2xl border border-charcoal/8 bg-bone p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-sm font-semibold text-charcoal">{match.title}</h2>
                  <span className="font-mono text-[10px] uppercase text-charcoal/38">{match.type} · {match.fit}</span>
                </div>
                <p className="mt-2 text-sm leading-6 text-charcoal/64">{match.why}</p>
                <p className="mt-2 text-xs leading-5 text-cocoa">{match.nextStep}</p>
                {match.link ? <a href={match.link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-charcoal/54 underline decoration-charcoal/20 underline-offset-4 hover:text-cocoa">Check source</a> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {result.draft ? (
        <section className="mt-5 rounded-2xl border border-clay/20 bg-clay/8 p-3">
          <p className="font-mono text-[10px] uppercase text-cocoa/55">{result.draft.title}</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-charcoal/70">{result.draft.body}</p>
        </section>
      ) : null}

      {result.translator ? (
        <section className="mt-5 rounded-2xl border border-[#1F7A3F]/20 bg-[#1F7A3F]/8 p-3">
          <p className="font-mono text-[10px] uppercase text-[#1F7A3F]">career translator</p>
          <div className="mt-3 grid gap-4">
            <article>
              <h2 className="text-sm font-semibold text-charcoal">Professional summary</h2>
              <p className="mt-1 text-sm leading-6 text-charcoal/68">{result.translator.professionalSummary}</p>
            </article>
            <article>
              <h2 className="text-sm font-semibold text-charcoal">Résumé bullets</h2>
              <ul className="mt-2 grid gap-1.5">
                {result.translator.resumeBullets.map((item) => (
                  <li key={item} className="text-sm leading-6 text-charcoal/66">• {item}</li>
                ))}
              </ul>
            </article>
            <article>
              <h2 className="text-sm font-semibold text-charcoal">Transferable skills</h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {result.translator.transferableSkills.map((skill) => (
                  <span key={skill} className="rounded-full bg-warm px-3 py-1.5 text-xs text-charcoal/62">{skill}</span>
                ))}
              </div>
            </article>
            <article>
              <h2 className="text-sm font-semibold text-charcoal">Career paths to explore</h2>
              <div className="mt-2 grid gap-2">
                {result.translator.careerPaths.map((path) => (
                  <div key={path.title} className="rounded-2xl bg-warm p-3">
                    <p className="text-sm font-semibold text-charcoal">{path.title}</p>
                    <p className="mt-1 text-xs leading-5 text-charcoal/60">{path.why}</p>
                    <p className="mt-1 text-xs leading-5 text-cocoa">First step: {path.firstStep}</p>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>
      ) : null}

      {result.actionPlan.length ? (
        <section className="mt-5">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">next moves</p>
          <div className="mt-2 grid gap-3">
            {result.actionPlan.map((item) => (
              <article key={item.title} className="border-t border-charcoal/10 pt-3">
                <h2 className="text-sm font-semibold text-charcoal">{item.title}</h2>
                <p className="mt-1 text-sm leading-6 text-charcoal/64">{item.body}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {result.followups.length ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {result.followups.map((followup) => (
            <button key={followup} type="button" onClick={() => onPrompt(followup)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {followup}
            </button>
          ))}
        </div>
      ) : null}

      <p className="mt-4 text-xs leading-5 text-charcoal/42">{result.groundedNote}</p>
      <ShareActions title={label} text={opportunityShareText(result, label)} filename={`${label.replace(/\s+/g, '-')}.txt`} />
    </div>
  );
}

function StudiesAnswer({ response, onPrompt }: { response: StudyResponse; onPrompt: (value: string) => void }) {
  if (response.mode === 'uncle_fallback') {
    return <UncleFallbackAnswer fallback={response} onPrompt={onPrompt} />;
  }

  if (response.mode === 'study_ask') {
    return (
      <div>
        <p className="font-mono text-[10px] uppercase text-charcoal/45">honest studies abroad</p>
        <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">Which school move?</h1>
        <p className="mt-3 text-sm leading-6 text-charcoal/72">{response.message}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {response.chips.map((chip) => (
            <button key={chip} type="button" onClick={() => onPrompt(chip)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {chip}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return <StudiesResultView result={response} onPrompt={onPrompt} />;
}

function StudiesResultView({ result, onPrompt }: { result: StudyResult; onPrompt: (value: string) => void }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">honest studies abroad · {result.topic}</p>
      <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">Study abroad, read properly.</h1>
      <p className="mt-3 text-sm leading-6 text-charcoal/72">{result.lead}</p>

      <section className="mt-4 rounded-2xl border border-charcoal/8 bg-bone p-3">
        <p className="font-mono text-[10px] uppercase text-charcoal/45">uncle’s read</p>
        <p className="mt-2 text-sm leading-6 text-charcoal/66">{result.read}</p>
      </section>

      {result.questions.length ? (
        <section className="mt-5 rounded-2xl border border-charcoal/8 bg-bone p-3">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">questions before we move</p>
          <ul className="mt-2 grid gap-2">
            {result.questions.map((question) => (
              <li key={question} className="text-sm leading-6 text-charcoal/70">• {question}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {result.essayScan ? (
        <section className="mt-5 rounded-2xl border border-clay/20 bg-clay/8 p-3">
          <p className="font-mono text-[10px] uppercase text-cocoa/55">essay scan</p>
          <p className="mt-2 text-sm leading-6 text-charcoal/70">{result.essayScan.verdict}</p>
          {result.essayScan.strongestLine ? <p className="mt-2 text-xs leading-5 text-charcoal/55">Strongest line Uncle noticed: “{result.essayScan.strongestLine}”</p> : null}
          <ul className="mt-3 grid gap-2">
            {result.essayScan.needsWork.map((item) => (
              <li key={item} className="text-xs leading-5 text-charcoal/62">• {item}</li>
            ))}
          </ul>
          <p className="mt-3 text-xs leading-5 text-cocoa">{result.essayScan.rewritePrompt}</p>
        </section>
      ) : null}

      <section className="mt-5">
        <p className="font-mono text-[10px] uppercase text-charcoal/45">action plan</p>
        <div className="mt-2 grid gap-3">
          {result.actionPlan.map((item) => (
            <article key={item.title} className="border-t border-charcoal/10 pt-3">
              <h2 className="text-sm font-semibold text-charcoal">{item.title}</h2>
              <p className="mt-1 text-sm leading-6 text-charcoal/64">{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      {result.links.length ? (
        <section className="mt-5 rounded-2xl border border-charcoal/8 bg-bone p-3">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">official + live checks</p>
          <div className="mt-2 grid gap-3">
            {result.links.map((link) => (
              <a key={link.title} href={link.url} target="_blank" rel="noreferrer" className="focus-ring block rounded-2xl bg-warm p-3 transition hover:bg-stone/20">
                <span className="text-sm font-semibold text-charcoal">{link.title}</span>
                <span className="mt-1 block text-xs leading-5 text-charcoal/56">{link.note}</span>
              </a>
            ))}
          </div>
        </section>
      ) : null}

      {result.followups.length ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {result.followups.map((followup) => (
            <button key={followup} type="button" onClick={() => onPrompt(followup)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {followup}
            </button>
          ))}
        </div>
      ) : null}

      <p className="mt-4 text-xs leading-5 text-charcoal/42">{result.groundedNote}</p>
      <ShareActions title="Honest Studies Abroad" text={studyShareText(result)} filename="honest-studies-abroad.txt" />
    </div>
  );
}

function TravelAnswer({ response, onPrompt }: { response: TravelResponse; onPrompt: (value: string) => void }) {
  if (response.mode === 'uncle_fallback') {
    return <UncleFallbackAnswer fallback={response} onPrompt={onPrompt} />;
  }

  if (response.mode === 'travel_ask') {
    return (
      <div>
        <p className="font-mono text-[10px] uppercase text-charcoal/45">{[response.country, 'choose the lens'].filter(Boolean).join(' · ')}</p>
        <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">{response.city}</h1>
        <p className="mt-3 text-sm leading-6 text-charcoal/72">{response.message}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {response.chips.map((chip) => (
            <button key={chip} type="button" onClick={() => onPrompt(chip)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {chip}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return <TravelResultView result={response} onPrompt={onPrompt} />;
}

function TravelResultView({ result, onPrompt }: { result: TravelResult; onPrompt: (value: string) => void }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">{[result.city.country, result.city.region, result.theme].filter(Boolean).join(' · ')}</p>
      <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">{result.city.name}</h1>
      <p className="mt-3 text-sm leading-6 text-charcoal/72">{result.lead}</p>

      {result.digression ? (
        <aside className="mt-4 rounded-2xl border border-clay/20 bg-clay/8 p-3">
          <p className="font-serif text-lg italic leading-6 text-cocoa">{result.digression}</p>
        </aside>
      ) : null}

      <section className="mt-4 rounded-2xl border border-charcoal/8 bg-bone p-3">
        <p className="font-mono text-[10px] uppercase text-charcoal/45">market read</p>
        <p className="mt-2 text-sm leading-6 text-charcoal/66">{result.marketRead}</p>
      </section>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <DataPoint value={result.lens.places.length} label="places" />
        <DataPoint value={result.lens.organizations.length} label="community" />
        <DataPoint value={result.lens.neighborhoods.length} label="areas" />
      </div>

      <div className="mt-5 grid gap-4">
        <MiniSection title="worth checking now" records={result.worthChecking} />
        {result.sections.map((section) => (
          <section key={section.title}>
            <p className="font-mono text-[10px] uppercase text-charcoal/45">{section.title}</p>
            <p className="mt-2 text-sm leading-6 text-charcoal/66">{section.body}</p>
            <div className="mt-3">
              <MiniSection title="grounded anchors" records={section.records} />
            </div>
          </section>
        ))}
      </div>

      {result.avoidOnlyClassics.length ? (
        <section className="mt-5 rounded-2xl border border-charcoal/8 bg-bone p-3">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">do not let it only be the classics</p>
          <p className="mt-2 text-sm leading-6 text-charcoal/62">These can orient you, but they should not be the whole read: {result.avoidOnlyClassics.join(', ')}.</p>
        </section>
      ) : null}

      {result.relocationLinks?.length ? (
        <section className="mt-5 rounded-2xl border border-charcoal/8 bg-bone p-3">
          <p className="font-mono text-[10px] uppercase text-charcoal/45">relocation live checks</p>
          <div className="mt-2 grid gap-3">
            {result.relocationLinks.map((link) => (
              <a key={link.title} href={link.url} target="_blank" rel="noreferrer" className="focus-ring block rounded-2xl bg-warm p-3 transition hover:bg-stone/20">
                <span className="text-sm font-semibold text-charcoal">{link.title}</span>
                <span className="mt-1 block text-xs leading-5 text-charcoal/56">{link.note}</span>
              </a>
            ))}
          </div>
        </section>
      ) : null}

      {result.followups.length ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {result.followups.map((followup) => (
            <button key={followup} type="button" onClick={() => onPrompt(followup)} className="focus-ring rounded-full border border-charcoal/10 bg-bone px-3 py-2 text-xs text-charcoal/62 transition hover:border-charcoal/25 hover:text-charcoal">
              {followup}
            </button>
          ))}
        </div>
      ) : null}

      <p className="mt-4 text-xs leading-5 text-charcoal/42">{result.groundedNote}</p>
      <ShareActions title={`Honest Travels: ${result.city.name}`} text={travelShareText(result)} filename={`honest-travels-${slugify(result.city.name)}.txt`} />
    </div>
  );
}

function LensAnswer({ lens, adjustments }: { lens: PlaceLens; adjustments: LensAdjustments }) {
  const lead = useMemo(() => {
    const summary = lens.city.summary || 'diaspora, food, community, movement, and everyday city texture';
    const emphasis = describeAdjustmentMix(adjustments);
    return `${lens.city.name} reads through ${summary}. With your dials set toward ${emphasis}, I would start with the records that carry those signals while still keeping the read grounded in what is curated.`;
  }, [lens, adjustments]);

  const adjustedRecords = useMemo(() => rankRecords([...lens.places, ...lens.organizations], lens, adjustments), [lens, adjustments]);
  const foodAndMarkets = useMemo(() => rankRecords([...lens.signals.food, ...lens.signals.markets], lens, adjustments).slice(0, 4), [lens, adjustments]);
  const culture = useMemo(() => rankRecords(lens.signals.culture, lens, adjustments).slice(0, 4), [lens, adjustments]);
  const community = useMemo(() => rankRecords(lens.signals.community, lens, adjustments).slice(0, 4), [lens, adjustments]);

  return (
    <div>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">{[lens.city.country, lens.city.region].filter(Boolean).join(' · ')}</p>
      <h1 className="mt-2 font-serif text-4xl leading-none tracking-[-0.04em]">{lens.city.name}</h1>
      <p className="mt-3 text-sm leading-6 text-charcoal/72">{lead}</p>
      <UncleDigression kind="travels" city={lens.city.name} />

      <div className="mt-4 grid grid-cols-3 gap-2">
        <DataPoint value={lens.places.length} label="places" />
        <DataPoint value={lens.organizations.length} label="community" />
        <DataPoint value={lens.neighborhoods.length} label="neighborhoods" />
      </div>

      <div className="mt-4 rounded-2xl border border-charcoal/8 bg-bone p-3">
        <p className="font-mono text-[10px] uppercase text-charcoal/45">active flavor mix</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {adjustmentLabels.map((item) => (
            <div key={item.key}>
              <div className="h-1.5 bg-charcoal/10">
                <div className="h-full bg-charcoal" style={{ width: `${adjustments[item.key]}%` }} />
              </div>
              <p className="mt-2 text-xs text-charcoal/56">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-4">
        <MiniSection title="Best match for your dials" records={adjustedRecords.slice(0, 4)} />
        <MiniSection title="Food and markets" records={foodAndMarkets} />
        <MiniSection title="Culture and memory" records={culture} />
        <MiniSection title="People and community" records={community} />
      </div>

      <ShareActions title={`Honest Travels: ${lens.city.name}`} text={placeShareText(lens, lead, adjustedRecords, foodAndMarkets, culture, community)} filename={`honest-travels-${slugify(lens.city.name)}.txt`} />
    </div>
  );
}

function ShareActions({ title, text, filename }: { title: string; text: string; filename: string }) {
  const [copied, setCopied] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  async function share() {
    if (navigator.share) {
      await navigator.share({ title, text }).catch(() => undefined);
      return;
    }
    await navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function download() {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function play() {
    if (voiceStatus === 'playing' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setVoiceStatus('idle');
      return;
    }

    setVoiceStatus('loading');
    try {
      const blob = await speechAudio(text.slice(0, 2600));
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setVoiceStatus('idle');
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setVoiceStatus('error');
      };
      await audio.play();
      setVoiceStatus('playing');
    } catch {
      setVoiceStatus('error');
    }
  }

  return (
    <div className="mt-5 flex flex-wrap gap-2 border-t border-charcoal/10 pt-4">
      <button type="button" onClick={() => void share()} className="focus-ring inline-flex items-center gap-2 rounded-full border border-charcoal/10 bg-bone px-3 py-2 font-mono text-[10px] uppercase text-charcoal/58 transition hover:border-charcoal/25 hover:text-charcoal" aria-label={`Share ${title}`}>
        <Share2 className="h-3.5 w-3.5" />
        {copied ? 'Copied' : 'Share'}
      </button>
      <button type="button" onClick={download} className="focus-ring inline-flex items-center gap-2 rounded-full border border-charcoal/10 bg-bone px-3 py-2 font-mono text-[10px] uppercase text-charcoal/58 transition hover:border-charcoal/25 hover:text-charcoal" aria-label={`Download ${title}`}>
        <Download className="h-3.5 w-3.5" />
        Download
      </button>
      <button type="button" onClick={() => void play()} disabled={voiceStatus === 'loading'} className="focus-ring inline-flex items-center gap-2 rounded-full border border-charcoal/10 bg-bone px-3 py-2 font-mono text-[10px] uppercase text-charcoal/58 transition hover:border-charcoal/25 hover:text-charcoal disabled:opacity-50" aria-label={`Listen to ${title}`}>
        <Volume2 className="h-3.5 w-3.5" />
        {voiceStatus === 'loading' ? 'Loading' : voiceStatus === 'playing' ? 'Stop' : voiceStatus === 'error' ? 'Voice unavailable' : 'Listen'}
      </button>
    </div>
  );
}

function MiniSection({ title, records }: { title: string; records: LensRecord[] }) {
  return (
    <section>
      <p className="font-mono text-[10px] uppercase text-charcoal/45">{title}</p>
      {records.length ? (
        <div className="mt-2 grid gap-3">
          {records.map((record) => (
            <article key={record.id} className="border-t border-charcoal/10 pt-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-charcoal">{record.name}</h2>
                <span className="font-mono text-[10px] uppercase text-charcoal/38">{record.type}</span>
              </div>
              {record.description ? <p className="mt-1 text-sm leading-6 text-charcoal/64">{record.description}</p> : null}
              {record.tags.length ? <p className="mt-2 text-xs text-charcoal/45">{record.tags.slice(0, 4).join(' · ')}</p> : null}
            </article>
          ))}
        </div>
      ) : <p className="mt-2 text-sm leading-6 text-charcoal/55">No grounded records in this category yet.</p>}
    </section>
  );
}

function placeShareText(lens: PlaceLens, lead: string, best: LensRecord[], food: LensRecord[], culture: LensRecord[], community: LensRecord[]) {
  return [
    `Honest Travels · ${lens.city.name}`,
    [lens.city.country, lens.city.region].filter(Boolean).join(' · '),
    '',
    lead,
    '',
    shareRecordSection('Best match', best.slice(0, 4)),
    shareRecordSection('Food and markets', food),
    shareRecordSection('Culture and memory', culture),
    shareRecordSection('People and community', community),
    '— UncleHonest'
  ].filter(Boolean).join('\n');
}

function travelShareText(result: TravelResult) {
  return [
    `Honest Travels · ${result.city.name}`,
    [result.city.country, result.city.region].filter(Boolean).join(' · '),
    '',
    `Theme: ${result.theme}`,
    '',
    result.lead,
    '',
    result.digression,
    '',
    `Market read: ${result.marketRead}`,
    '',
    shareRecordSection('Worth checking now', result.worthChecking),
    result.avoidOnlyClassics.length ? `Do not let it only be the classics: ${result.avoidOnlyClassics.join(', ')}` : '',
    result.relocationLinks?.length ? `\nRelocation live checks\n${result.relocationLinks.map((link) => `- ${link.title}: ${link.url} — ${link.note}`).join('\n')}` : '',
    '',
    result.groundedNote,
    '— UncleHonest'
  ].filter(Boolean).join('\n');
}

function studyShareText(result: StudyResult) {
  return [
    `Honest Studies Abroad · ${result.topic}`,
    '',
    result.lead,
    '',
    result.read,
    '',
    result.questions.length ? `Questions before we move\n${result.questions.map((question) => `- ${question}`).join('\n')}` : '',
    result.essayScan ? [
      'Essay scan',
      result.essayScan.verdict,
      result.essayScan.strongestLine ? `Strongest line: ${result.essayScan.strongestLine}` : '',
      result.essayScan.needsWork.map((item) => `- ${item}`).join('\n'),
      result.essayScan.rewritePrompt
    ].filter(Boolean).join('\n') : '',
    result.actionPlan.length ? `Action plan\n${result.actionPlan.map((item) => `- ${item.title}: ${item.body}`).join('\n')}` : '',
    result.links.length ? `Official + live checks\n${result.links.map((link) => `- ${link.title}: ${link.url} — ${link.note}`).join('\n')}` : '',
    '',
    result.groundedNote,
    '— UncleHonest'
  ].filter(Boolean).join('\n');
}

function uncleFallbackShareText(fallback: UncleFallback) {
  return [
    'UncleHonest · to-do note',
    '',
    `“${fallback.proverb}”`,
    '',
    fallback.message,
    fallback.todoPrompt,
    '',
    '— UncleHonest'
  ].join('\n');
}

function serviceShareText(answer: ServiceAnswer) {
  return [
    `About UncleHonest · ${answer.title}`,
    '',
    answer.lead,
    '',
    answer.sections.map((section) => `${section.title}\n${section.body}`).join('\n\n'),
    '',
    answer.links.length ? `Links\n${answer.links.map((link) => `- ${link.title}: ${link.url} — ${link.note}`).join('\n')}` : '',
    '',
    '— UncleHonest'
  ].filter(Boolean).join('\n');
}

function investorShareText(result: InvestorScopeResult) {
  return [
    'Honest Invests · investor(o)scope',
    `${result.overall}/100 · ${result.verdict}`,
    '',
    result.rationale,
    '',
    `Market math: ${result.market_math.reachable_people} reachable · $${Math.round(result.market_math.price_point)} price · $${result.market_math.projected_monthly}/mo projected`,
    result.market_math.notes,
    result.returns_speed.applies ? `Returns speed: ${result.returns_speed.verdict} — ${result.returns_speed.note}` : '',
    '',
    'Readiness signals',
    result.scores.map((score) => `- ${score.label}: ${score.score}/100. ${score.note} Improve: ${score.improve}`).join('\n'),
    result.next_steps.length ? `\nNext steps\n${result.next_steps.map((step) => `- ${step}`).join('\n')}` : '',
    '',
    '— UncleHonest'
  ].filter(Boolean).join('\n');
}

function opportunityShareText(result: OpportunityResult, label: string) {
  return [
    `${label} · ${result.topic}`,
    '',
    result.lead,
    '',
    result.read,
    '',
    result.matches.length ? `Matches\n${result.matches.map((match) => `- ${match.title} (${match.type}, ${match.fit}): ${match.why} Next: ${match.nextStep}${match.link ? ` ${match.link}` : ''}`).join('\n')}` : '',
    result.draft ? `\n${result.draft.title}\n${result.draft.body}` : '',
    result.translator ? [
      '\nCareer translator',
      `Professional summary\n${result.translator.professionalSummary}`,
      `Résumé bullets\n${result.translator.resumeBullets.map((item) => `- ${item}`).join('\n')}`,
      `Transferable skills\n${result.translator.transferableSkills.map((item) => `- ${item}`).join('\n')}`,
      `Career paths\n${result.translator.careerPaths.map((path) => `- ${path.title}: ${path.why} First step: ${path.firstStep}`).join('\n')}`
    ].join('\n\n') : '',
    result.actionPlan.length ? `\nNext moves\n${result.actionPlan.map((item) => `- ${item.title}: ${item.body}`).join('\n')}` : '',
    '',
    result.groundedNote,
    '— UncleHonest'
  ].filter(Boolean).join('\n');
}

function shareRecordSection(title: string, records: LensRecord[]) {
  if (!records.length) return '';
  return `${title}\n${records.map((record) => `- ${record.name}${record.type ? ` · ${record.type}` : ''}${record.description ? ` — ${record.description}` : ''}`).join('\n')}\n`;
}

function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'uncle-honest';
}

function DataPoint({ value, label }: { value: number | string; label: string }) {
  return (
    <div className="rounded-2xl border border-charcoal/8 bg-bone p-3">
      <p className="font-serif text-2xl leading-none">{value}</p>
      <p className="mt-1 font-mono text-[10px] uppercase text-charcoal/42">{label}</p>
    </div>
  );
}

function verdictClass(verdict: string) {
  const normalized = verdict.toLowerCase();
  if (normalized.includes('fundable')) return 'bg-olive/18 text-charcoal';
  if (normalized.includes('clarity')) return 'bg-clay/18 text-cocoa';
  return 'bg-charcoal/10 text-charcoal/70';
}

function modeLabel(mode: Mode) {
  if (mode === 'place') return 'honest travels';
  return `honest ${mode}`;
}

function modeIcon(mode: Mode) {
  if (mode === 'invests') return '$';
  if (mode === 'grants') return <Gift className="h-3.5 w-3.5" />;
  if (mode === 'benefits') return <HandHeart className="h-3.5 w-3.5" />;
  if (mode === 'careers') return <BriefcaseBusiness className="h-3.5 w-3.5" />;
  if (mode === 'studies') return <GraduationCap className="h-3.5 w-3.5" />;
  return <Globe2 className="h-3.5 w-3.5" />;
}

function cleanPlaceQuery(text: string) {
  return text
    .replace(/^tell me about\s+/i, '')
    .replace(/^what should i know about\s+/i, '')
    .replace(/^read\s+/i, '')
    .replace(/\s+for me$/i, '')
    .replace(/^what is\s+/i, '')
    .replace(/\s+like\??$/i, '')
    .replace(/^give me\s+/i, '')
    .replace(/\s+through a diaspora lens$/i, '')
    .trim() || text;
}

function looksLikeNewPlaceQuery(text: string) {
  return /^(tell me about|what should i know about|read|what is|give me)\b/i.test(text)
    || /\b(lagos|accra|kingston|jamaica|nairobi|são paulo|sao paulo|atlanta|ghana|nigeria|kenya|brazil)\b/i.test(text);
}

function isAboutUncleQuery(text: string) {
  return /\b(who are you|what are you|what is uncle|UncleHonest|about uncle|about this|what is this|what do you do|how does this work|service|birthright|birth right|softerbread|501c3|501\\(c\\)\\(3\\)|nonprofit|non-profit|free|support|who made|who built)\b/i.test(text);
}

function buildServiceAnswer(text: string): ServiceAnswer {
  const wantsBirthright = /\b(birthright|birth right|softerbread|501|nonprofit|non-profit|support|free)\b/i.test(text);

  return {
    title: wantsBirthright ? 'Have we met? I’m UncleHonest.' : 'Have we met? I’m UncleHonest.',
    lead: 'UncleHonest is a free, human-led, AI-powered tool — the well-travelled uncle/auntie voice in your pocket: warm, direct, and allergic to vague advice. Uncle helps people read places, pressure-test ideas, and study abroad with more context than a search result usually gives.',
    sections: [
      {
        title: 'what uncle does',
        body: 'There are six lanes: Honest Invests reads whether someone might fund this “thing”; Honest Grants helps shape funder fit and grant language; Honest Benefits helps sort possible support programs; Honest Careers helps with work story and next moves; Honest Studies Abroad helps with schools, scholarships, essays, visas, and study permits; Honest Travels helps people land well through diaspora-aware reads of places, neighborhoods, markets, food, and community.'
      },
      {
        title: 'why birthright and softerbread',
        body: 'UncleHonest is offered as a free, human-led, AI-powered tool by Softerbread, the financial co-op initiative of The Birthright Project. The spirit is practical: help people from African, Caribbean, Latin American, and diasporic communities make moves with better information, less loneliness, and more honest guidance.'
      },
      {
        title: 'what uncle is not',
        body: 'Uncle is not a lawyer, immigration officer, financial advisor, admissions officer, realtor, or travel agent. Uncle can help you frame the right questions, read the situation, find official links, and avoid lazy advice — but important decisions still need official sources and human verification.'
      },
      {
        title: 'how to support',
        body: 'The concept is meant to stay easy and free. If someone wants to support the work, the About Uncle panel includes a Stripe support link.'
      }
    ],
    links: [
      { title: 'Softerbread', url: 'https://www.thebirthrightproject.org/softerbread', note: 'The financial co-op initiative connected to UncleHonest.' },
      { title: 'The Birthright Project', url: 'https://www.thebirthrightproject.org/', note: 'The 501(c)(3) nonprofit home behind the broader work.' }
    ],
    chips: ['who is behind UncleHonest?', 'how does Uncle stay honest?', 'where does the data come from?', 'how can I support Uncle?', 'what can Uncle help me think through?']
  };
}

function makeLocalUncleFallback(topic: string, detail?: string): UncleFallback {
  const cleaned = topic.slice(0, 90);
  const lane = detail && /not have enough grounded data|city|country|place/i.test(detail) ? 'travels' : 'this lane';
  return {
    mode: 'uncle_fallback',
    proverb: 'A person who admits the road is dark is already carrying a lamp.',
    message: `I don’t know enough about “${cleaned}” in ${lane} yet to answer honestly. I could guess, but guessing is how bad advice puts on good shoes.`,
    todoPrompt: 'Say a little more about what you wanted Uncle to know, and we can add it to Uncle’s to-do list.',
    chips: ['add this to Uncle’s to-do list', 'tell Uncle more', 'try Honest Travels', 'try Honest Studies']
  };
}

function describeAdjustmentMix(adjustments: LensAdjustments) {
  const top = adjustmentLabels
    .map((item) => ({ label: item.label.toLowerCase(), value: adjustments[item.key] }))
    .filter((item) => item.value >= 55)
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .map((item) => item.label);

  if (!top.length) return 'a quieter, more general local read';
  if (top.length === 1) return `a stronger ${top[0]} read`;
  return top.slice(0, -1).join(', ') + ` and ${top[top.length - 1]}`;
}

function rankRecords(records: LensRecord[], lens: PlaceLens, adjustments: LensAdjustments) {
  const cityHay = `${lens.city.name} ${lens.city.country} ${lens.city.region || ''} ${lens.city.summary || ''}`.toLowerCase();
  const unique = new Map<string, LensRecord>();
  for (const record of records) unique.set(record.id, record);

  return [...unique.values()]
    .map((record, index) => {
      const hay = `${record.name} ${record.type} ${record.description || ''} ${record.tags.join(' ')} ${record.neighborhood || ''} ${cityHay}`.toLowerCase();
      const score =
        scoreFlavor(hay, adjustments.african, ['african', 'afro', 'black', 'diaspora', 'pan-african', 'nigerian', 'ghana', 'kenya', 'lagos', 'accra', 'nairobi', 'textiles', 'craft']) +
        scoreFlavor(hay, adjustments.caribbean, ['caribbean', 'jamaica', 'kingston', 'reggae', 'music', 'island', 'coastal', 'family']) +
        scoreFlavor(hay, adjustments.latinAmerican, ['latin', 'brazil', 'brazilian', 'afro-brazilian', 'são paulo', 'sao paulo', 'liberdade', 'design', 'street art']) +
        scoreFlavor(hay, adjustments.localEveryday, ['market', 'food', 'walking', 'park', 'groceries', 'neighborhood', 'coffee', 'local', 'trade', 'family']);

      return { record, index, score };
    })
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((item) => item.record);
}

function scoreFlavor(hay: string, strength: number, tokens: string[]) {
  if (strength <= 0) return 0;
  const multiplier = strength / 20;
  return tokens.reduce((score, token) => score + (hay.includes(token) ? multiplier : 0), 0);
}

function initialMode(): Mode {
  const value = new URLSearchParams(window.location.search).get('mode')?.toLowerCase();
  if (value === 'grants' || value === 'grant') return 'grants';
  if (value === 'benefits' || value === 'benefit') return 'benefits';
  if (value === 'careers' || value === 'career') return 'careers';
  if (value === 'studies') return 'studies';
  if (value === 'travels' || value === 'travel' || value === 'place') return 'place';
  return 'invests';
}
